import { useEffect, useState } from 'react';
import { Alert, Badge, Modal, Spinner } from 'react-bootstrap';
import { getBookingSettlement } from '../api';

const SETTLEMENT_STATUS_VARIANT = { pending_review: 'warning', approved: 'success', rejected: 'danger', adjusted: 'info' };

// Owner Bookings & Cancellation v2 doc, Story 12 - popup (not just a status badge) showing the
// owner or customer exactly why/when/by-whom a booking was cancelled, plus the refund breakdown,
// its settlement status, and Finance's reviewer comments once reviewed. `booking` is passed in
// (already has `.cancellation` from the list/detail response the caller already fetched) so only
// the settlement itself needs its own request.
export default function CancellationDetailsModal({ booking, show, onHide }) {
  const [settlement, setSettlement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show || !booking?._id) return;
    setSettlement(null);
    setError('');
    setLoading(true);
    getBookingSettlement(booking._id)
      .then(setSettlement)
      .catch((err) => {
        if (err.response?.status !== 404) setError('Could not load refund/settlement detail.');
      })
      .finally(() => setLoading(false));
  }, [show, booking?._id]);

  if (!booking) return null;
  const c = booking.cancellation || {};

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">Cancellation details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3 p-2 rounded bg-light small">
          <div><strong>Cancelled by:</strong> {c.cancelledBy || 'unknown'}</div>
          {c.cancelledAt && <div><strong>Cancelled at:</strong> {new Date(c.cancelledAt).toLocaleString()}</div>}
          <div><strong>Reason:</strong> {c.reason || '(no reason recorded)'}</div>
          {c.forceMajeureClaimed && (
            <div className="text-info mt-1">
              Force-majeure claimed{c.forceMajeureEvidenceUrl ? ` - evidence: ${c.forceMajeureEvidenceUrl}` : ''}
            </div>
          )}
        </div>

        {loading && <Spinner animation="border" size="sm" role="status" />}
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}

        {settlement && (
          <div className="p-2 rounded border small">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Refund &amp; settlement</strong>
              <Badge bg={SETTLEMENT_STATUS_VARIANT[settlement.status] || 'secondary'}>
                {settlement.status.replace(/_/g, ' ')}
              </Badge>
            </div>
            <div>Rental amount refunded: ₹{settlement.rentalAmountRefunded}</div>
            <div>Platform fee refunded: ₹{settlement.platformFeeRefunded}</div>
            <div>Delivery charge refunded: ₹{settlement.deliveryChargeRefunded}</div>
            <div>Deposit refunded: ₹{settlement.depositRefunded}</div>
            {settlement.reviewerNotes && (
              <div className="mt-2">
                <strong>Finance comments:</strong>
                <div className="text-muted">{settlement.reviewerNotes}</div>
              </div>
            )}
            {settlement.overrideJustification && (
              <div className="mt-1">
                <strong>Override justification:</strong>
                <div className="text-muted">{settlement.overrideJustification}</div>
              </div>
            )}
          </div>
        )}
        {!loading && !settlement && !error && (
          <Alert variant="light" className="py-2 mb-0 small">No refund/settlement record for this booking.</Alert>
        )}
      </Modal.Body>
    </Modal>
  );
}
